import {
    Box,
    Button,
    Container,
    Grid,
    Paper,
    Typography,
    Table, TableHead, TableRow, TableCell, TableBody,
    TablePagination,
    Stack,
    TableSortLabel,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import axiosClient from "../../../../../lib/axios";
import DisplayNotFoundInfo from "../../../../../components/DisplayNotFindInfo";
import ScreenBanner from "../../../../../components/ScreenBanner";
import { formatarValorMonetario } from "../../../../../helpers/masks";
import PermissionGuard from "../../../../../components/PermissionGuard";
import { IDataNotaFiscalEntrada } from "../../../../../types/api/fiscal/pesquisar-nota-fiscal-entrada";
import moment from "moment";
import DialogFiltrosNfs from "../DialogFiltrosNfs";
import { useNotaFiscal } from "../../ModalDetalheNotaFiscalContext";
import SearchWithFilters from "../../../../../components/BasicComponents/SearchWithFilters";
import useDebounce from "../../../../../lib/useDebounce";
import ChipList from "../../../../../components/BasicComponents/ChipList";
import SkeletonListarNotas from "./SkeletonListarNotas";
import { getChipFilterItems } from "../../../../../helpers/getChipFilterItems";

function ListarNotasFiscaisContent() {

    const [palavraChave, setPalavraChave] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filtros, setFiltros] = useState<TFiltrosNfs>({});
    const [openFiltros, setOpenFiltros] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ column: 'data_emissao'; direction: 'asc' | 'desc' }>({ column: 'data_emissao', direction: 'desc' });

    const inputRef = useRef<HTMLInputElement>(null);

    const { openModal } = useNotaFiscal();

    const debouncedSearchTerm = useDebounce(palavraChave);

    const { data, isFetching } = useQuery(
        ["pesquisar-notas-fiscais", debouncedSearchTerm, page, rowsPerPage, filtros, sortConfig],
        async () => {
            const params = {
                ...filtros,
                pesquisar: debouncedSearchTerm,
                page: page + 1,
                limit: rowsPerPage,
                ...(sortConfig && {
                    ordering: JSON.stringify([{
                        column: sortConfig.column,
                        direction: sortConfig.direction.toUpperCase()
                    }])
                })
            };
            const res = await axiosClient.get("/fiscal/pesquisar-notas-fiscais-entrada", { params });
            return res.data;
        },
        { keepPreviousData: true }
    );

    const { requestNota, dataNota } = useMemo(
        () => ({
            requestNota: data,
            dataNota: data?.data,
        }),
        [data]
    );

    const handleSearch = useCallback(() => {
        setPage(0);
    }, []);

    useEffect(() => {
        const handleEnter = (e: KeyboardEvent) => {
            if (e.key === "Enter") {
                setPage(0);
            }
        };
        window.addEventListener("keydown", handleEnter);
        return () => window.removeEventListener("keydown", handleEnter);
    }, []);

    const handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSort = (field: 'data_emissao') => {
        setSortConfig(prevSort => {
            if (prevSort?.column === field) {
                return {
                    column: field,
                    direction: prevSort.direction === 'asc' ? 'desc' : 'asc'
                };
            }
            return { column: field, direction: 'asc' };
        });
    };

    const handleDeleteFilter = (item: string) => {
        const newFiltros = { ...filtros };

        // Se o item deletado for relacionado ao valor
        if (item.startsWith('Valor:')) {
            delete newFiltros.valor_total_maior_igual;
            delete newFiltros.valor_total_menor_igual;
            delete newFiltros.valor;
        } else {
            // Para outros filtros, encontra a chave correspondente
            Object.entries(filtros).forEach(([key, value]) => {
                const label = filtersChipLabels[key as keyof typeof filtersChipLabels] || key;
                const displayValue = Array.isArray(value) ? value.join(', ') : value;
                const chipText = `${label}: ${displayValue}`;

                if (chipText === item) {
                    delete newFiltros[key as keyof TFiltrosNfs];
                }
            });
        }

        setFiltros(newFiltros);
    }

    const chipItems = useMemo(() => {
        return getChipFilterItems(
            filtros,
            filtersChipLabels,
            ['valor_total_maior_igual', 'valor_total_menor_igual']
        );
    }, [filtros]);

    return (
        <Box sx={{ background: "#F5F6FA", minHeight: "100vh" }}>
            <Container maxWidth="lg" sx={{ mt: 5 }}>
                <Typography textAlign="center" variant="h5" mb={3}>
                    Notas fiscais de entrada
                </Typography>
                <Grid container spacing={3} justifyContent="center">
                    <Grid size={{ xs: 12 }}>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 2,
                            }}
                        >
                            <SearchWithFilters
                                palavraChave={palavraChave}
                                textFieldProps={{
                                    inputRef: inputRef,
                                    label: "Pesquisar",
                                    placeholder: "Pesquise por emissor, número ou chave da nota",
                                    size: "medium",
                                }}
                                pesquisar={handleSearch}
                                setTermoPesquisa={setPalavraChave}
                                handleOpenFilters={setOpenFiltros}
                            />

                            <ChipList
                                items={chipItems}
                                onDelete={handleDeleteFilter}
                            />
                            {
                                isFetching && (
                                    <SkeletonListarNotas />
                                )
                            }

                            {
                                !isFetching && dataNota && (
                                    <>
                                        <Box sx={{ width: "100%", overflowX: "auto" }}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ width: '25%' }}><b>DANFE</b></TableCell>
                                                        <TableCell><b>Emitente</b></TableCell>
                                                        <TableCell>
                                                            <TableSortLabel
                                                                active={sortConfig?.column === 'data_emissao'}
                                                                direction={sortConfig?.column === 'data_emissao' ? sortConfig.direction : 'asc'}
                                                                onClick={() => handleSort('data_emissao')}
                                                            >
                                                                <b>Emissão</b>
                                                            </TableSortLabel>
                                                        </TableCell>
                                                        <TableCell align="right"><b>Valor</b></TableCell>
                                                        <TableCell><b>Status</b></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {dataNota.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={5}>
                                                                <DisplayNotFoundInfo
                                                                    label="Nenhuma nota fiscal encontrada" />
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        dataNota.map((row: IDataNotaFiscalEntrada) => (
                                                            <TableRow key={row.id} hover>
                                                                <TableCell>
                                                                    <Button
                                                                        variant="text"
                                                                        sx={{
                                                                            color: "primary.main",
                                                                            textDecoration: "underline",
                                                                            fontWeight: "bold",
                                                                            fontSize: "1rem",
                                                                            padding: 0,
                                                                            minWidth: "unset"
                                                                        }}
                                                                        onClick={() => {

                                                                            openModal(row.id.toString());
                                                                        }}
                                                                    >
                                                                        Nº {row.numero_nota || 'N/A'}/{row.serie_nota || 'N/A'}
                                                                    </Button>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ color: "#888", fontSize: "0.85rem", mt: 0.5 }}
                                                                    >
                                                                        {row.chave ? `Chave: ${row.chave}` : 'Chave: N/A'}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="left">
                                                                    <Stack gap={0.5}>
                                                                        <Typography variant="body2" fontWeight={500}>
                                                                            {`${row.tipo_emissor && row.codigo_emissor ? `${row.tipo_emissor}${row.codigo_emissor} - ` : ''} ${row.nome_emissor || 'N/A'}`}
                                                                        </Typography>
                                                                    </Stack>
                                                                </TableCell>
                                                                <TableCell align="left">
                                                                    <Stack gap={0.5}>
                                                                        <Typography variant="body2" fontWeight={500}>
                                                                            {row.data_emissao ? moment(row.data_emissao).format("DD/MM/YYYY") : 'N/A'}
                                                                        </Typography>
                                                                    </Stack>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Typography variant="body1">
                                                                        {row.valor_total ? formatarValorMonetario(Number(row.valor_total)) : "-"}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    {row.status || 'Desconhecido'}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    )}
                                                </TableBody>
                                            </Table>
                                            <Stack direction="row" justifyContent="flex-end">
                                                <TablePagination
                                                    component="div"
                                                    count={requestNota?.total}
                                                    page={page}
                                                    onPageChange={handleChangePage}
                                                    rowsPerPage={rowsPerPage}
                                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                                    labelRowsPerPage="Linhas por página"
                                                />
                                            </Stack>
                                        </Box>
                                    </>
                                )}
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
            {openFiltros &&
                <DialogFiltrosNfs
                    open={openFiltros}
                    onClose={() => setOpenFiltros(false)}
                    onConfirm={(filtros) => {
                        setOpenFiltros(false);
                        setPage(0);
                        setFiltros(filtros);
                    }}
                    filtrosAtuais={filtros}
                />}
        </Box>
    );
}

export default function ListarNotasFiscais() {
    return (
        <>
            <ScreenBanner color="#1E2E6B" title="Fiscal" />
            <PermissionGuard permissionId="fiscal.notas-fiscais.listar-todas">
                <ListarNotasFiscaisContent />
            </PermissionGuard>
        </>
    );
}

export type TFiltrosNfs = {
    data_emissao_inicio?: string;
    data_emissao_fim?: string;
    nome_emissor_like?: string;
    valor?: string;
    valor_total_maior_igual?: number;
    valor_total_menor_igual?: number;
    status?: string[];
}

const filtersChipLabels: { [key in keyof Omit<TFiltrosNfs, 'valor_total_maior_igual' | 'valor_total_menor_igual'>]: string } = {
    data_emissao_inicio: 'Data emissão início',
    data_emissao_fim: 'Data emissão fim',
    nome_emissor_like: 'Emissor',
    valor: 'Valor',
    status: 'Status',
};